import {
  type PostComposedWebRequestBody,
  type PostComposedWebResponseBody,
} from 'src/api/checkout-widgets-client';
import { type Experiment } from 'src/types/experiment';
import { type WebRequest, type WebResponse } from 'src/types/widget-data';
import {
  type InstallmentOffer,
  type ServiceFeePolicy,
  type WidgetOfferPeriod,
  type WidgetOffers,
} from 'src/types/widget-offers';
import { convertToSnakeCase } from 'src/utils/convert-to-snake-case';

export type WebWireRequest = PostComposedWebRequestBody;
export type WebWireResponse = PostComposedWebResponseBody;

const SERVICE_FEE_POLICIES: readonly ServiceFeePolicy[] = [
  'overall',
  'downpayment_only',
  'per_installment',
  'annuity',
];
const PERIOD_SIZES: readonly WidgetOfferPeriod['size'][] = [
  'day',
  'week',
  'month',
];
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' &&
  value <= Number.MAX_VALUE &&
  value >= -Number.MAX_VALUE;

const isInt32 = (value: unknown): value is number =>
  isFiniteNumber(value) &&
  Math.floor(value) === value &&
  value >= -2147483648 &&
  value <= 2147483647;

const adaptExperiment = (value: unknown): Experiment | null => {
  if (
    !isRecord(value) ||
    typeof value.name !== 'string' ||
    typeof value.value !== 'string'
  ) {
    return null;
  }

  return { name: value.name, value: value.value };
};

const adaptInstallment = (value: unknown): InstallmentOffer | null => {
  if (
    !isRecord(value) ||
    !isFiniteNumber(value.amount) ||
    !isInt32(value.paymentsCount) ||
    !isInt32(value.priority) ||
    !isFiniteNumber(value.totalFees) ||
    (value.isLimitedTimeOffer !== undefined &&
      typeof value.isLimitedTimeOffer !== 'boolean') ||
    !isRecord(value.period) ||
    PERIOD_SIZES.indexOf(value.period.size as WidgetOfferPeriod['size']) ===
      -1 ||
    !isInt32(value.period.value) ||
    value.period.value < 1
  ) {
    return null;
  }

  const serviceFeePolicy =
    SERVICE_FEE_POLICIES.indexOf(value.serviceFeePolicy as ServiceFeePolicy) !==
    -1
      ? (value.serviceFeePolicy as ServiceFeePolicy)
      : undefined;
  const serviceFeePercentage =
    typeof value.service_fee_percentage === 'string'
      ? value.service_fee_percentage
      : undefined;

  return {
    amount: value.amount,
    paymentsCount: value.paymentsCount,
    priority: value.priority,
    totalFees: value.totalFees,
    period: {
      size: value.period.size as WidgetOfferPeriod['size'],
      value: value.period.value,
    },
    ...(typeof value.isLimitedTimeOffer === 'boolean'
      ? { isLimitedTimeOffer: value.isLimitedTimeOffer }
      : {}),
    ...(serviceFeePolicy ? { serviceFeePolicy } : {}),
    ...(serviceFeePercentage
      ? { service_fee_percentage: serviceFeePercentage }
      : {}),
    ...(typeof value.subventionCampaignId === 'string'
      ? { subventionCampaignId: value.subventionCampaignId }
      : {}),
  };
};

const adaptOffers = (value: unknown): WidgetOffers => {
  if (!isRecord(value)) {
    return { installments: [] };
  }

  const installments = Array.isArray(value.installments)
    ? value.installments
        .map(adaptInstallment)
        .filter((offer): offer is InstallmentOffer => offer !== null)
    : [];
  const payInFull = isRecord(value.payInFull) ? value.payInFull : undefined;
  const monthlyBilling = isRecord(value.monthlyBilling)
    ? value.monthlyBilling
    : undefined;
  let monthlyBillingAvailability: boolean | undefined;
  if (typeof monthlyBilling?.available === 'boolean') {
    monthlyBillingAvailability = monthlyBilling.available;
  } else if (typeof monthlyBilling?.paymentDate === 'string') {
    monthlyBillingAvailability = true;
  }

  return {
    installments,
    ...(typeof payInFull?.available === 'boolean'
      ? { payInFull: { available: payInFull.available } }
      : {}),
    ...(monthlyBillingAvailability !== undefined
      ? {
          monthlyBilling: {
            available: monthlyBillingAvailability,
            ...(typeof monthlyBilling?.paymentDate === 'string'
              ? { paymentDate: monthlyBilling.paymentDate }
              : {}),
          },
        }
      : {}),
  };
};

export const createEmptyWebResponse = (): WebResponse => ({
  experiments: {
    experiments: [],
    entityId: '',
  },
  aid: '',
  offers: { installments: [] },
});

/**
 * Convert the schema-first backend payload into the tolerant domain shape used
 * by the rendering store. The adapter also accepts the historical camelCase
 * entity id used by native payloads while all new HTTP fixtures stay wire-shaped.
 */
export const adaptWebResponse = (value: unknown): WebResponse => {
  if (!isRecord(value)) {
    return createEmptyWebResponse();
  }

  const experimentsValue = isRecord(value.experiments)
    ? value.experiments
    : undefined;
  const experiments = Array.isArray(experimentsValue?.experiments)
    ? experimentsValue.experiments
        .map(adaptExperiment)
        .filter((experiment): experiment is Experiment => experiment !== null)
    : [];
  let entityId = '';
  if (typeof experimentsValue?.entity_id === 'string') {
    entityId = experimentsValue.entity_id;
  } else if (typeof experimentsValue?.entityId === 'string') {
    entityId = experimentsValue.entityId;
  }

  return {
    experiments: { experiments, entityId },
    aid: typeof value.aid === 'string' ? value.aid : '',
    offers: adaptOffers(value.offers),
  };
};

const toSnakeCaseRecord = (
  value: Record<string, string | undefined>
): Record<string, string> => {
  const converted = convertToSnakeCase(value);
  const result: Record<string, string> = {};

  Object.keys(converted).forEach((key) => {
    const item = converted[key];

    if (item !== undefined) result[key] = item;
  });

  return result;
};

export const toWebWireRequest = (request: WebRequest): WebWireRequest => ({
  public_key: request.publicKey ?? '',
  webview: request.webview ?? false,
  ...(request.ab
    ? {
        ab: {
          entity: request.ab.entity,
          entity_id: request.ab.entityId,
          ...(request.ab.platform ? { platform: request.ab.platform } : {}),
          ...(request.ab.filters
            ? { filters: toSnakeCaseRecord(request.ab.filters) }
            : {}),
        },
      }
    : {}),
  ...(request.merchantCode ? { merchant_code: request.merchantCode } : {}),
  ...(request.currency ? { currency: request.currency } : {}),
  ...(request.amount !== undefined ? { amount: request.amount } : {}),
});
